/**
 * WebSocket Store
 *
 * Socket.io 연결 상태 및 이벤트 구독 관리
 * - 이벤트 핸들러 레지스트리 패턴: 소켓 구독은 한 번만, 핸들러는 교체 가능
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { Socket } from 'socket.io-client';
import {
  getSocket,
  connectWebSocket,
  disconnectWebSocket,
  joinOrganization,
  leaveOrganization,
  joinInspection as joinInspectionRoom,
  leaveInspection as leaveInspectionRoom,
  ServerEvents,
} from '../lib/websocket';

/**
 * 이벤트 핸들러 타입
 */
type EventHandler<T = unknown> = (data: T) => void;

/**
 * WebSocket 상태 인터페이스
 */
interface WebSocketState {
  // 상태
  isConnected: boolean;
  isConnecting: boolean;
  isAuthenticated: boolean; // 서버 인증 완료 여부
  error: string | null;
  currentOrganizationId: string | null;
  currentInspectionId: string | null;
  _reconnected: boolean; // 재접속 발생 플래그
  reconnectionAttempts: number; // 현재 재연결 시도 횟수
  maxReconnectAttemptsReached: boolean; // 최대 재연결 시도 초과 여부

  // 내부용
  _socket: Socket | null;
  _pendingOrganizationId: string | null; // 인증 대기 중인 조직 ID
  _subscribedEvents: Set<string>; // 구독 중인 이벤트 목록
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _eventHandlers: Map<string, EventHandler<any>>; // 이벤트별 핸들러 레지스트리

  // 액션
  connect: () => void;
  disconnect: () => void;
  switchOrganization: (companyId: string) => void;
  joinInspection: (inspectionId: string) => Promise<void>;
  leaveInspection: (inspectionId: string) => void;
  retryReconnection: () => void; // 수동 재연결

  // 이벤트 구독 (레거시 - 하위 호환성)
  subscribe: <T>(event: `${ServerEvents}`, handler: EventHandler<T>) => () => void;

  // 핸들러 레지스트리 (권장 방식)
  registerHandler: <T>(event: `${ServerEvents}`, handler: EventHandler<T>) => void;
  unregisterHandler: (event: `${ServerEvents}`) => void;

  // 구독 관리
  hasSubscription: (event: `${ServerEvents}`) => boolean;
  clearAllSubscriptions: () => void;

  // 내부용 상태 업데이트
  _setConnected: (connected: boolean) => void;
  _setConnecting: (connecting: boolean) => void;
  _setAuthenticated: (authenticated: boolean) => void;
  _setError: (error: string | null) => void;
}

/**
 * WebSocket Store
 */
export const useWebSocketStore = create<WebSocketState>((set, get) => ({
  // 초기 상태
  isConnected: false,
  isConnecting: false,
  isAuthenticated: false,
  error: null,
  currentOrganizationId: null,
  currentInspectionId: null,
  _reconnected: false,
  _socket: null,
  _pendingOrganizationId: null,
  _subscribedEvents: new Set<string>(),
  _eventHandlers: new Map(),
  reconnectionAttempts: 0,
  maxReconnectAttemptsReached: false,

  /**
   * WebSocket 연결
   */
  connect: () => {
    const { isConnected, isConnecting } = get();
    if (isConnected || isConnecting) return;

    set({ isConnecting: true, error: null, isAuthenticated: false });

    const socket = connectWebSocket();
    set({ _socket: socket });

    // 연결 이벤트 핸들러
    socket.on('connect', () => {
      console.log('[WebSocket] Connected:', socket.id);
      set({ isConnected: true, isConnecting: false, error: null });
    });

    socket.on('disconnect', (reason) => {
      console.log('[WebSocket] Disconnected:', reason);
      set({ isConnected: false, isConnecting: false, isAuthenticated: false });
    });

    // 재연결 시도
    socket.io.on('reconnect_attempt', () => {
      const { reconnectionAttempts: current } = get();
      const newAttempt = current + 1;
      console.log(`[WebSocket] Reconnect attempt ${newAttempt}/20...`);
      set({ reconnectionAttempts: newAttempt });
    });

    // 재연결 성공
    socket.io.on('reconnect', () => {
      console.log('[WebSocket] Reconnected, re-authenticating...');
      set({ _reconnected: true, reconnectionAttempts: 0, maxReconnectAttemptsReached: false });
      // 재연결 후 서버 인증 대기 (AUTHENTICATED 이벤트에서 Room 재입장 처리)
      const { currentOrganizationId } = get();
      if (currentOrganizationId) {
        set({ _pendingOrganizationId: currentOrganizationId, isAuthenticated: false });
      }
    });

    // 재연결 실패 (최대 횟수 초과)
    socket.io.on('reconnect_failed', () => {
      console.error('[WebSocket] Reconnection failed after max attempts');
      set({ maxReconnectAttemptsReached: true, error: '연결 실패 — 수동 재연결이 필요합니다' });
    });

    socket.on('connect_error', (err) => {
      console.error('[WebSocket] Connection error:', err.message);
      set({
        isConnected: false,
        isConnecting: false,
        isAuthenticated: false,
        error: err.message,
      });
    });

    // 서버 인증 완료 이벤트
    socket.on(ServerEvents.AUTHENTICATED, async () => {
      console.log('[WebSocket] Authenticated');
      set({ isAuthenticated: true });

      // 대기 중인 조직 Room 입장 처리
      const { _pendingOrganizationId } = get();
      if (_pendingOrganizationId) {
        const result = await joinOrganization(_pendingOrganizationId);
        if (result.success) {
          set({ currentOrganizationId: _pendingOrganizationId, _pendingOrganizationId: null });
        } else {
          console.error('[WebSocket] Failed to join pending organization:', result.message);
          set({ _pendingOrganizationId: null });
        }
      }

      // 대기 중인 점검 Room 재입장
      const { currentInspectionId } = get();
      if (currentInspectionId) {
        joinInspectionRoom(currentInspectionId).then(result => {
          if (!result.success) {
            console.error('[WebSocket] Failed to rejoin inspection room:', result.message);
            set({ currentInspectionId: null });
          }
        });
      }
    });

    // 서버에서 강제 연결 해제
    socket.on(ServerEvents.FORCE_DISCONNECT, (data: { message: string }) => {
      console.warn('[WebSocket] Force disconnect:', data.message);
      socket.disconnect();
      set({ isConnected: false, isAuthenticated: false, error: data.message });
    });

    // 연결 오류 (서버에서 인증 실패 등)
    // 토큰 만료로 인한 인증 실패는 HTTP API의 토큰 갱신 완료 후 자동 재연결됨
    // (websocket.ts의 onTokenRefreshed 구독)
    socket.on(ServerEvents.CONNECTION_ERROR, (data: { message: string }) => {
      console.error('[WebSocket] Server error:', data.message);
      set({ error: data.message });
    });
  },

  /**
   * WebSocket 연결 해제
   */
  disconnect: () => {
    const { currentOrganizationId, clearAllSubscriptions } = get();

    // 모든 구독 해제
    clearAllSubscriptions();

    // 현재 조직 Room 퇴장
    if (currentOrganizationId) {
      leaveOrganization(currentOrganizationId);
    }

    disconnectWebSocket();
    set({
      isConnected: false,
      isConnecting: false,
      isAuthenticated: false,
      currentOrganizationId: null,
      _pendingOrganizationId: null,
      _reconnected: false,
      _socket: null,
    });
  },

  /**
   * 조직 변경 시 Room 전환
   */
  switchOrganization: async (companyId: string) => {
    const { currentOrganizationId, isConnected, isAuthenticated } = get();

    // 연결되지 않은 경우
    if (!isConnected) {
      set({ _pendingOrganizationId: companyId });
      return;
    }

    // 연결되었지만 아직 인증되지 않은 경우 (대기열에 추가)
    if (!isAuthenticated) {
      console.log('[WebSocket] Not authenticated yet, queuing organization:', companyId);
      set({ _pendingOrganizationId: companyId });
      return;
    }

    // 이전 조직 Room 퇴장
    if (currentOrganizationId && currentOrganizationId !== companyId) {
      leaveOrganization(currentOrganizationId);
    }

    // 새 조직 Room 입장
    console.log('[WebSocket] Joining organization room:', companyId);
    const result = await joinOrganization(companyId);
    if (result.success) {
      console.log('[WebSocket] Successfully joined organization room:', companyId);
      set({ currentOrganizationId: companyId });
    } else {
      console.error('[WebSocket] Failed to join organization:', result.message);
    }
  },

  /**
   * 점검 Room 입장 (상세 페이지용)
   */
  joinInspection: async (inspectionId: string) => {
    const { isConnected, isAuthenticated, currentInspectionId } = get();

    if (!isConnected || !isAuthenticated) {
      console.log('[WebSocket] Cannot join inspection - not connected/authenticated');
      return;
    }

    // 이미 같은 점검 Room에 있는 경우
    if (currentInspectionId === inspectionId) {
      return;
    }

    // 이전 점검 Room 퇴장
    if (currentInspectionId) {
      leaveInspectionRoom(currentInspectionId);
    }

    // 새 점검 Room 입장
    const result = await joinInspectionRoom(inspectionId);
    if (result.success) {
      console.log('[WebSocket] Joined inspection room:', inspectionId);
      set({ currentInspectionId: inspectionId });
    } else {
      console.error('[WebSocket] Failed to join inspection:', result.message);
    }
  },

  /**
   * 점검 Room 퇴장
   */
  leaveInspection: (inspectionId: string) => {
    const { currentInspectionId } = get();

    if (currentInspectionId === inspectionId) {
      leaveInspectionRoom(inspectionId);
      console.log('[WebSocket] Left inspection room:', inspectionId);
      set({ currentInspectionId: null });
    }
  },

  /**
   * 수동 재연결 시도 (최대 횟수 초과 후)
   */
  retryReconnection: () => {
    const { _socket } = get();
    if (_socket) {
      console.log('[WebSocket] Manual reconnection retry...');
      set({ reconnectionAttempts: 0, maxReconnectAttemptsReached: false, error: null });
      _socket.connect();
    }
  },

  /**
   * 핸들러 등록 (권장 방식)
   * - 소켓 구독은 이벤트당 한 번만 설정
   * - 핸들러는 레지스트리에 저장되어 교체 가능
   * - 컴포넌트 리렌더링/리마운트에도 구독이 유지됨
   */
  registerHandler: <T>(event: `${ServerEvents}`, handler: EventHandler<T>) => {
    const socket = getSocket();
    const { _subscribedEvents, _eventHandlers } = get();

    // 핸들러 레지스트리에 저장 (교체)
    _eventHandlers.set(event, handler);

    // 아직 이 이벤트에 소켓 구독이 없으면 설정
    if (!_subscribedEvents.has(event)) {
      console.log('[WebSocket] Setting up socket subscription for:', event);

      // 소켓 구독: 레지스트리의 핸들러를 호출
      socket.on(event, (data: T) => {
        const currentHandler = get()._eventHandlers.get(event);
        if (currentHandler) {
          currentHandler(data);
        }
      });

      _subscribedEvents.add(event);
    }
  },

  /**
   * 핸들러 해제
   * - 핸들러만 제거, 소켓 구독은 유지
   */
  unregisterHandler: (event: `${ServerEvents}`) => {
    const { _eventHandlers } = get();
    _eventHandlers.delete(event);
  },

  /**
   * 이벤트 구독 (레거시 - 하위 호환성)
   * 여러 컴포넌트가 동일 이벤트를 구독할 수 있음
   * @returns 구독 해제 함수
   */
  subscribe: <T>(event: `${ServerEvents}`, handler: EventHandler<T>) => {
    const socket = getSocket();
    const { _subscribedEvents } = get();

    // 핸들러 등록 (다른 컴포넌트의 핸들러는 유지)
    console.log('[WebSocket] Subscribing to event:', event);
    socket.on(event, handler);
    _subscribedEvents.add(event);

    // 구독 해제 함수 반환 (해당 핸들러만 제거)
    return () => {
      socket.off(event, handler);
      // 다른 핸들러가 있을 수 있으므로 _subscribedEvents에서 삭제하지 않음
    };
  },

  /**
   * 특정 이벤트 구독 여부 확인
   */
  hasSubscription: (event: `${ServerEvents}`) => {
    return get()._subscribedEvents.has(event);
  },

  /**
   * 모든 구독 해제 (연결 종료 시 호출)
   */
  clearAllSubscriptions: () => {
    const socket = getSocket();
    const { _subscribedEvents, _eventHandlers } = get();

    _subscribedEvents.forEach((event) => {
      socket.removeAllListeners(event);
    });
    _subscribedEvents.clear();
    _eventHandlers.clear();
  },

  // 내부용 상태 업데이트
  _setConnected: (connected) => set({ isConnected: connected }),
  _setConnecting: (connecting) => set({ isConnecting: connecting }),
  _setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),
  _setError: (error) => set({ error }),
}));

// 선택자 훅
export const useIsWebSocketConnected = () => useWebSocketStore((state) => state.isConnected);
export const useWebSocketReconnected = () => useWebSocketStore((state) => state._reconnected);
export const useWebSocketError = () => useWebSocketStore((state) => state.error);
export const useWebSocketReconnectionAttempts = () => useWebSocketStore((state) => state.reconnectionAttempts);
export const useWebSocketMaxReconnectAttemptsReached = () => useWebSocketStore((state) => state.maxReconnectAttemptsReached);
export const useWebSocketStatus = () => useWebSocketStore(
  useShallow((state) => ({
    isConnected: state.isConnected,
    isConnecting: state.isConnecting,
    isAuthenticated: state.isAuthenticated,
    reconnectionAttempts: state.reconnectionAttempts,
    maxReconnectAttemptsReached: state.maxReconnectAttemptsReached,
    error: state.error,
    retryReconnection: state.retryReconnection,
  }))
);
